// src/components/PostDetailsModal.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider.js';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import {
  FaRoute,
  FaLocationArrow,
  FaSearchLocation,
  FaUser,
  FaBox,
  FaCalendarAlt,
  FaClock,
  FaRuler,
  FaCarSide,
  FaExclamationTriangle,
  FaUsers,
  FaInfoCircle,
  FaWeightHanging,
  FaShippingFast,
  FaRegPaperPlane,
  FaCar,
  FaMapMarkedAlt,
  FaListAlt,
  FaEuroSign
} from 'react-icons/fa';
import { format } from 'date-fns';
import RouteMatches from './RouteMatches';
import PostRouteMapView from './PostRouteMapView';
import { CustomButton7, CloseButton1 } from './Templates';
import { useChat } from './Chat/ChatContext';
import Modal from './common/Modal';
import Spinner from './common/Spinner';
import { getCorrectRouteEndpoints } from '../utils/routeUtils';
import { styleStaticMapUrl } from '../utils/mapStyle';
import PostCardPagination from './PostCardPagination';
import MessageConfirmationModal from './common/MessageConfirmationModal';
import DateSelectionModal from './common/DateSelectionModal';
import './PostDetailsModal.css';

// Helper function to parse coordinates from PostgreSQL point format or text
const parseCoordinates = (coordString) => {
  if (!coordString) return null;
  
  try {
    // Handle PostgreSQL point format: "(lng,lat)"
    if (coordString.startsWith('(') && coordString.endsWith(')')) {
      const coords = coordString.substring(1, coordString.length - 1).split(',');
      if (coords.length === 2) {
        // For Google Maps, we need "lat,lng" format
        return `${coords[1].trim()},${coords[0].trim()}`;
      }
    }
    
    // Handle text address (let Google geocode it)
    return coordString;
  } catch (error) {
    console.error('Error parsing coordinates:', error, coordString);
    return coordString; // Return as is, let Google Maps API handle it
  }
};

// Helper functions for safe numeric handling
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  try {
    const result = parseFloat(value);
    return isNaN(result) ? 0 : result;
  } catch (error) {
    console.error('Error parsing float:', error, value);
    return 0;
  }
};

const formatFloat = (value, precision = 1) => {
  return safeParseFloat(value).toFixed(precision);
};

/**
 * Modal component to display post or package details with user stats pagination
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {string} props.id - ID of the post, package, or request
 * @param {string} props.type - Type of content: 'post', 'package', or 'request'
 */
export default function PostDetailsModal({ isOpen, onClose, id, type, postId, postType }) {
  // For matches display in the map view
  const [matches, setMatches] = useState([]);
  // Handle the case where we might receive either id or postId
  const actualId = id || postId;
  const actualType = type || postType;

  // Navigation state for additional content (map/matches) - separate from pagination
  const [showMapView, setShowMapView] = useState(false);
  const [showMatchesView, setShowMatchesView] = useState(false);
  // When the map "maximize" is active we grow the whole modal to fullscreen
  // (reusing Modal's modal-fullscreen size) and lift the post-card height cap.
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  // Never reopen the modal stuck in fullscreen map mode.
  useEffect(() => {
    if (!isOpen) setIsMapExpanded(false);
  }, [isOpen]);
  
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { openChatWithConversation } = useChat();
  
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Message confirmation modal state
  const [messageConfirmModal, setMessageConfirmModal] = useState({
    isOpen: false,
    post: null,
    isLoading: false
  });

  // Date selection modal state (for recurring posts)
  const [dateSelectionModal, setDateSelectionModal] = useState({
    isOpen: false,
    post: null
  });
  
  // Add event listeners for refreshing modal and matches
  useEffect(() => {
    const handleRefreshModal = (event) => {
      const { id, type } = event.detail;
      if (id && actualId === id) {
        // Re-fetch data for this modal
        console.log('Refreshing modal data for:', id, type);
        // Close and re-open the modal to refresh data
        onClose();
        setTimeout(() => {
          const openEvent = new CustomEvent('openPostDetailsModal', {
            detail: { id, type }
          });
          window.dispatchEvent(openEvent);
        }, 200);
      }
    };
    
    const handleRefreshMatches = (event) => {
      const { radius } = event.detail;
      if (radius && postData) {
        // Just refresh the matches
        fetchMatches();
        // Update the radius in postData
        setPostData(prev => ({
          ...prev,
          search_radius_km: radius
        }));
      }
    };
    
    window.addEventListener('refreshPostDetailsModal', handleRefreshModal);
    window.addEventListener('refreshMatches', handleRefreshMatches);
    
    return () => {
      window.removeEventListener('refreshPostDetailsModal', handleRefreshModal);
      window.removeEventListener('refreshMatches', handleRefreshMatches);
    };
  }, [actualId, isOpen, postData]);
  
  // Fetch post, package, or request data
  useEffect(() => {
    // Skip if modal is not open
    if ((!isOpen && id) || !actualId) return;
    
    console.log('PostDetailsModal initialized with type:', actualType, 'and id:', actualId);
    const fetchData = async () => {
      if (!actualId) {
        setError('No ID specified');
        setLoading(false);
        return;
      }
      
      // Safely handle undefined type with default value
      const safeType = actualType || 'post';

      try {
        setLoading(true);
        setError(null);
        
        // Log the type and ID for debugging
        console.log('Fetching data with type:', safeType, 'and id:', actualId);
        
        const isPackage = safeType === 'package';
        const isRequest = safeType === 'request';
        let table;
        
        if (isPackage) {
          table = 'packages';
        } else if (isRequest) {
          table = 'event_ride_requests';
        } else {
          table = 'posts';
        }
        
        console.log('Using table:', table);
        
        let query;
        
        if (isRequest) {
          // For ride requests, we need to join with profiles, but using a different approach
          query = supabase
            .from(table)
            .select(`
              *,
              events(*)
            `)
            .eq('id', parseInt(actualId, 10));
            
          console.log('Executing request query:', JSON.stringify(query));
            
          // After we get the request, we'll fetch the passenger profile separately
        } else {
          // For posts and packages
          const tableName = isPackage ? 'packages' : 'posts';
          
          query = supabase
            .from(table)
            .select(`
              *,
              profiles!${tableName}_user_id_fkey(username, avatar_url)
            `);
          
          if (isPackage) {
            // For packages, use UUID as is
            console.log('Querying packages table with id:', actualId);
            console.log('isPackage is true, using ID as UUID, type =', typeof actualId);
            query = query.eq('id', actualId);
          } else {
            // For posts, ensure ID is an integer and handle potential NaN values
            const parsedId = parseInt(actualId, 10);
            if (isNaN(parsedId)) {
              throw new Error('Invalid post ID');
            }
            query = query.eq('id', parsedId);
          }
        }
          
        console.log('Executing query:', query);
        // Check if the query returns any data first without using .single()
        const { data: checkData, error: checkError } = await query;
        
        if (checkError) {
          console.error('Error checking data:', checkError);
          throw checkError;
        }
        
        console.log('Check query returned:', checkData?.length || 0, 'rows', checkData);
        
        if (!checkData || checkData.length === 0) {
          setError(`${isRequest ? 'Request' : isPackage ? 'Package' : 'Post'} not found (ID: ${actualId})`);
          setLoading(false);
          return;
        }
        
        if (checkData.length > 1) {
          console.warn('Multiple rows returned, using first item');
        }
        
        // Use the first item if multiple were returned
        const data = checkData[0];
          
        // fetchError handling removed as we're not using .single() anymore
        
        if (!data) {
          setError(`${isPackage ? 'Package' : 'Post'} not found`);
          setLoading(false);
          return;
        }
        
        // Add markers to indicate the content type
        data.isPackage = isPackage;
        data.isRequest = isRequest;
        
        if (isRequest) {
          // For requests, we need to fetch the passenger profile separately
          try {
            // First, get the passenger_id from the request data
            const passengerId = data.passenger_id;
            
            if (passengerId) {
              // Fetch the passenger profile
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('id, username, avatar_url')
                .eq('id', passengerId)
                .single();
                
              if (profileError) {
                console.error('Error fetching passenger profile:', profileError);
              }
              
              if (profileData) {
                // Add the profile data to our request data
                data.passenger_profile = profileData;
              }
            }
          } catch (profileFetchError) {
            console.error('Error in passenger profile fetch:', profileFetchError);
          }
          
          data.role = 'Request';
          // For ride requests, adjust the data structure to be compatible with the UI
          data.profiles = data.passenger_profile || {};
          data.address_from = data.pickup_location || '';
          data.address_to = data.events?.[0]?.location || '';
          data.event = data.events?.[0] || {};
          data.travel_date = data.events?.[0]?.start_date || new Date().toISOString();
        } else if (isPackage) {
          data.role = 'Package';
        }
        
        console.log('Fetched data:', data);
        // Additional debugging for ride requests
        if (isRequest) {
          console.log('Ride request pickup_coordinates:', data.pickup_coordinates);
          console.log('Ride request event data:', data.events?.[0]);
        }
        
        // Fix route endpoints if this is a post or package with waypoints
        if (!isRequest) {
          try {
            const correctedData = await getCorrectRouteEndpoints(data, isPackage);
            console.log('Route endpoints corrected for PostDetailsModal:', correctedData.route_corrected ? 'Yes' : 'No');
            setPostData(correctedData);
          } catch (routeError) {
            console.error('Error correcting route endpoints in PostDetailsModal:', routeError);
            setPostData(data);
          }
        } else {
          setPostData(data);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [actualId, actualType, isOpen, id, type]);
  
  const formatDate = (date) => {
    if (date) {
      return format(new Date(date), "dd/MM/yyyy");
    }
    return t('posts.not_specified');
  };

  const formatTime = (time) => {
    if (!time) return t('posts.not_specified');
    // Convert "HH:mm:ss" to "HH:mm"
    return time.split(':').slice(0, 2).join(':');
  };

  const formatAddress = (street, city, country) => {
    const parts = [street, city, country].filter(Boolean);
    return parts.join(", ");
  };

  // Format recurrence pattern for display
  const formatRecurrencePattern = (post) => {
    if (post.travel_type !== 'recurring' || !post.recurrence_pattern) {
      return null;
    }

    const pattern = typeof post.recurrence_pattern === 'string'
      ? JSON.parse(post.recurrence_pattern)
      : post.recurrence_pattern;

    if (!pattern || !pattern.weekdays || pattern.weekdays.length === 0) {
      return null;
    }

    const weekdays = pattern.weekdays.sort((a, b) => a - b);
    const weekdayNames = {
      1: t('weekdays.short_monday') || 'Mo',
      2: t('weekdays.short_tuesday') || 'Tu',
      3: t('weekdays.short_wednesday') || 'We',
      4: t('weekdays.short_thursday') || 'Th',
      5: t('weekdays.short_friday') || 'Fr',
      6: t('weekdays.short_saturday') || 'Sa',
      7: t('weekdays.short_sunday') || 'Su'
    };

    // Check for special patterns
    if (weekdays.length === 7) {
      return t('recurring.every_day');
    }
    if (weekdays.length === 5 && weekdays.every(d => d >= 1 && d <= 5)) {
      // Show only first and last day abbreviations (Mo-Fr for Mon-Fri)
      return `${weekdayNames[1]}-${weekdayNames[5]}`;
    }
    if (weekdays.length === 2 && weekdays.includes(6) && weekdays.includes(7)) {
      // Show only first and last day abbreviations (Sa-Su for Sat-Sun)
      return `${weekdayNames[6]}-${weekdayNames[7]}`;
    }

    // Otherwise, show day abbreviations
    return weekdays.map(d => weekdayNames[d]).join(', ');
  };
  
  // Fetch matches for the map view
  const fetchMatches = useCallback(async (overrideRadius = null) => {
    if (!postData || !postData.id || !postData.route_polyline) return;

    try {
      // Use override radius if provided, otherwise use postData radius
      const radiusKm = overrideRadius !== null ? overrideRadius : (postData.search_radius_km || 10);

      console.log('Fetching matches with radius:', radiusKm, 'km');

      // Clear old matches immediately
      setMatches([]);

      const { data: routeMatchesData, error } = await supabase.rpc('find_route_matches', {
        p_route_id: parseInt(postData.id, 10),
        p_is_package: postData.isPackage || false,
        p_package_id: postData.isPackage ? postData.id : null,
        p_group_ride_id: null,
        p_is_group_ride: false,
        p_radius_km: parseFloat(radiusKm),
        p_role: null // Show ALL roles - users can filter themselves
      });

      if (error) {
        console.error('Error fetching matches for map:', error);
        return;
      }

      // Process the matches and filter out current user's posts
      const processedMatches = routeMatchesData?.map(match => ({
        ...match,
        distance_km: parseFloat(match.distance_km || '0'),
        start_point_lat: parseFloat(match.start_point_lat || '0'),
        start_point_lng: parseFloat(match.start_point_lng || '0'),
        end_point_lat: parseFloat(match.end_point_lat || '0'),
        end_point_lng: parseFloat(match.end_point_lng || '0')
      })).filter(match => match.user_id !== user?.id) || [];

      console.log('Fetched matches for map with radius', radiusKm + 'km:', processedMatches.length);
      setMatches(processedMatches);
    } catch (error) {
      console.error('Exception in fetchMatches:', error);
    }
  }, [postData, user?.id]);
  
  // Fetch matches when viewing own posts
  useEffect(() => {
    const isOwnPost = user?.id === postData?.user_id;
    
    if (isOwnPost && postData && postData.route_polyline) {
      fetchMatches();
    }
  }, [postData, user, fetchMatches]);
  
  const handleMatchClick = (match) => {
    console.log('Selected match:', match);
    // Ensure all numeric values are properly parsed
    const processedMatch = {
      ...match,
      distance_km: typeof match.distance_km === 'string' ? parseFloat(match.distance_km) : match.distance_km,
      start_point_lat: typeof match.start_point_lat === 'string' ? parseFloat(match.start_point_lat) : match.start_point_lat,
      start_point_lng: typeof match.start_point_lng === 'string' ? parseFloat(match.start_point_lng) : match.start_point_lng,
      end_point_lat: typeof match.end_point_lat === 'string' ? parseFloat(match.end_point_lat) : match.end_point_lat,
      end_point_lng: typeof match.end_point_lng === 'string' ? parseFloat(match.end_point_lng) : match.end_point_lng
    };
    
    // Handle different behaviors depending on which view is active
    if (showMapView) {
      // Just update the selected match for display
      setSelectedMatch(processedMatch);
    } else if (showMatchesView) {
      // Open new modal with match details instead of switching to MAP tab
      if (match.match_id && match.match_type) {
        console.log('PostDetailsModal dispatching openPostDetailsModal event from POSTS section:',
          match.match_id, match.match_type);
          
        // Close current modal and open the new one for the match
        onClose();
        
        // Short delay to ensure current modal is closed
        setTimeout(() => {
          // Dispatch event to open new modal
          const openModalEvent = new CustomEvent('openPostDetailsModal', {
            detail: {
              id: match.match_id,
              type: match.match_type
            }
          });
          window.dispatchEvent(openModalEvent);
        }, 100);
      } else {
        // Fallback to original behavior if match_id or match_type is missing
        setShowMatchesView(false);
        setShowMapView(true);
        setSelectedMatch(processedMatch);
      }
    }
  };
  
  // Function to open details for a specific match
  const openMatchDetails = (match) => {
    if (!match || !match.match_id || !match.match_type) {
      console.error('Invalid match data for opening details');
      return;
    }
    
    console.log('Opening match details:', match);
    
    // Store current modal state to restore if needed
    const currentModalData = { id: actualId, type: actualType };
    
    // Close current modal and open the new one for the match
    onClose();
    
    // Short delay to ensure current modal is closed
    setTimeout(() => {
      // Use window.history.replaceState to update URL without navigation
      if (typeof window !== 'undefined') {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('modal', 'post-details');
          url.searchParams.set('id', match.match_id);
          url.searchParams.set('type', match.match_type);
          window.history.replaceState({}, '', url.toString());
        } catch (error) {
          console.error('Error updating URL:', error);
        }
      }
      
      // Now dispatch a custom event to trigger modal opening in parent component
      // and explicitly set the correct ID and type based on the match
      console.log('Dispatching openPostDetailsModal with:', { id: match.match_id, type: match.match_type });
      const openModalEvent = new CustomEvent('openPostDetailsModal', {
        detail: {
          id: match.match_id,
          type: match.match_type
        }
      });
      window.dispatchEvent(openModalEvent);
      
      // Force navigation to refresh the modal as a fallback method
      try {
        const baseUrl = window.location.pathname;
        window.history.pushState({}, '', `${baseUrl}?modal=post-details&id=${match.match_id}&type=${match.match_type}`);
        // Trigger the popstate event to help any component listening for URL changes
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
      } catch (navError) {
        console.error('Error with navigation fallback:', navError);
      }
    }, 100);
  };
  
  // Listen for the openMatchDetails event
  useEffect(() => {
    const handleOpenMatchDetails = (event) => {
      const { match } = event.detail;
      if (match) {
        openMatchDetails(match);
      }
    };
    
    window.addEventListener('openMatchDetails', handleOpenMatchDetails);
    
    return () => {
      window.removeEventListener('openMatchDetails', handleOpenMatchDetails);
    };
  }, [actualId, actualType]); // Include dependencies needed by openMatchDetails
  
  // Handle message button click - show appropriate modal based on post type
  const handleMessageClick = (post) => {
    // For recurring posts (not packages), show date selection modal first
    if (!post.isPackage && post.travel_type === 'recurring') {
      setDateSelectionModal({
        isOpen: true,
        post: post
      });
    } else {
      // For one-time posts or packages, show regular confirmation modal
      setMessageConfirmModal({
        isOpen: true,
        post: post,
        isLoading: false
      });
    }
  };

  // Handle date selection for recurring posts
  const handleDateSelected = (occurrenceData) => {
    const post = dateSelectionModal.post;
    if (!post) return;

    // Close date selection modal
    setDateSelectionModal({
      isOpen: false,
      post: null
    });

    // Open message confirmation modal with selected occurrence
    setMessageConfirmModal({
      isOpen: true,
      post: {
        ...post,
        selectedOccurrenceId: occurrenceData.occurrenceId,
        selectedOccurrenceDate: occurrenceData.date,
        selectedOccurrenceTime: occurrenceData.time
      },
      isLoading: false
    });
  };

  // Handle confirmed message send
  const handleConfirmMessage = async () => {
    const post = messageConfirmModal.post;
    if (!post) return;

    try {
      // Set loading state
      setMessageConfirmModal(prev => ({
        ...prev,
        isLoading: true
      }));

      if (!user) {
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.login_required')
        });
        onClose();
        navigate('/login');
        return;
      }

      let result;
      let rpcError;

      if (post.isPackage) {
        const response = await supabase.rpc(
          'start_package_conversation',
          {
            p_user_id: user.id,
            p_other_user_id: post.user_id,
            p_package_id: post.id,
            p_message_text: null
          }
        );
        result = response.data;
        rpcError = response.error;
      } else {
        const response = await supabase.rpc(
          'start_post_conversation',
          {
            p_user_id: user.id,
            p_other_user_id: post.user_id,
            p_post_id: parseInt(post.id, 10),
            p_message_text: null,
            p_occurrence_id: post.selectedOccurrenceId || null  // Pass occurrence_id for recurring posts
          }
        );
        result = response.data;
        rpcError = response.error;
      }

      if (rpcError) {
        console.error('Error from conversation function:', rpcError);
        throw rpcError;
      }

      if (!result || !result.conversation_id) {
        if (result?.error) {
          throw new Error(result.error);
        } else {
          throw new Error('Failed to create or find conversation');
        }
      }

      // If we're hitting conversation limits, show a message
      if (result.error === 'Monthly chat limit reached') {
        addToast({
          type: 'error',
          title: t('errors.limit_reached'),
          description: t('chat.limit_reached_message', { max: result.monthly_limit || 5 })
        });

        // Close modals
        setMessageConfirmModal({ isOpen: false, post: null, isLoading: false });

        // Provide upgrade option
        const upgradeNow = window.confirm(t('chat.upgrade_subscription_prompt'));
        if (upgradeNow) {
          onClose();
          navigate('/subscription');
          return;
        }
        return;
      }

      // Make sure conversation is visible if it was hidden
      try {
        const { data: visibilityData } = await supabase
          .from('conversation_visibility')
          .select('*')
          .eq('conversation_id', result.conversation_id)
          .eq('user_id', user.id)
          .eq('status', 'hidden')
          .maybeSingle();

        if (visibilityData) {
          await supabase
            .from('conversation_visibility')
            .update({
              status: 'visible',
              updated_at: new Date().toISOString()
            })
            .eq('conversation_id', result.conversation_id)
            .eq('user_id', user.id);
        }
      } catch (visibilityError) {
        console.error('Error updating visibility:', visibilityError);
        // Continue even if this fails
      }

      // Open the chat
      openChatWithConversation(result.conversation_id);

      // Only show toast and update chat count for NEW conversations
      if (result.is_new && result.remaining_chats !== undefined) {
        addToast({
          type: 'info',
          title: t('chat.conversation_started'),
          description: t('chat.remaining_chats', { count: result.remaining_chats })
        });

        console.log('Dispatching chatCountUpdated event with:', {
          remaining: result.remaining_chats,
          limit: result.monthly_limit,
          count: result.monthly_count
        });

        const chatCountEvent = new CustomEvent('chatCountUpdated', {
          detail: {
            remaining: result.remaining_chats,
            limit: result.monthly_limit,
            count: result.monthly_count
          }
        });
        window.dispatchEvent(chatCountEvent);
      }

      // Close modals and main modal
      setMessageConfirmModal({ isOpen: false, post: null, isLoading: false });
      onClose();

    } catch (error) {
      console.error('Error starting conversation:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('chat.error_conversation_start')
      });

      // Reset loading state on error
      setMessageConfirmModal(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };
  
  // Get the modal title based on content type and role
  const getModalTitle = () => {
    if (!postData) return t('posts.details');

    if (postData.isPackage) return t('posts.package_details');
    if (postData.isRequest) return t('posts.ride_request_details');

    // Use specific translations for driver and passenger
    if (postData.role === 'Driver') return t('posts.driver_post_details');
    if (postData.role === 'Passenger') return t('posts.passenger_post_details');

    return t('posts.post_details');
  };
  
  // Render the static route map for the details section
  const renderStaticMap = () => {
    if (postData.isRequest) {
      // For ride requests, generate a static map URL based on pickup location and event location
      return (
        <div className="pdm-request-map">
          {(postData.pickup_coordinates || postData.pickup_location) && postData.event ? (
            <img
              src={`https://maps.googleapis.com/maps/api/staticmap?size=600x300&markers=color:green|label:A|${parseCoordinates(postData.pickup_coordinates) || postData.pickup_location || ''}&markers=color:red|label:B|${parseCoordinates(postData.event?.location_coordinates) || postData.event?.location || ''}&path=color:0x0000FF|weight:5|${parseCoordinates(postData.pickup_coordinates) || postData.pickup_location || ''}|${parseCoordinates(postData.event?.location_coordinates) || postData.event?.location || ''}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
              alt={t('chat.route_map')}
              className="pdm-route-map"
              onError={(e) => {
                e.target.src = '/placeholder-map.jpg';
                console.error('Error loading Google Maps image');
              }}
            />
          ) : (
            <img
              src={styleStaticMapUrl(postData.map_image_url) || '/placeholder-map.jpg'}
              alt={t('chat.route_map')}
              className="pdm-route-map"
            />
          )}
        </div>
      );
    } else {
      // For regular posts or packages
      return (
        <div className="pdm-map-image-container">
          <img
            src={styleStaticMapUrl(postData.map_image_url) || '/placeholder-map.jpg'}
            alt={t('chat.route_map')}
            className="pdm-route-map"
          />
        </div>
      );
    }
  };
  
  // Render the default post content (Page 0 of pagination)
  const renderPostContent = () => {
    return (
      <div className="pdm-default-content">
        {/* Static Map */}
        <div className="pdm-map-container">
          {renderStaticMap()}
        </div>
        
        {/* Route details */}
        <div className="pdm-post-content">
          <div className="pdm-post-detail">
            <span className="pdm-detail-icon"><FaRoute /></span>
            <div className="pdm-detail-content">
              <div className="pdm-detail-label">{t('posts.route')}:</div>
              <div className="pdm-location-detail">
                <span className="pdm-location-marker pdm-from">S</span>
                <span className="pdm-location-text">
                  {postData.isRequest ? 
                    postData.pickup_location :
                    formatAddress(
                      postData.address_from_street,
                      postData.address_from_city,
                      postData.address_from_country
                    )
                  }
                </span>
              </div>
              <div className="pdm-location-detail">
                <span className="pdm-location-marker pdm-to">E</span>
                <span className="pdm-location-text">
                  {postData.isRequest ? 
                    (postData.event?.location || 'Event location') :
                    formatAddress(
                      postData.address_to_street,
                      postData.address_to_city,
                      postData.address_to_country
                    )
                  }
                </span>
              </div>
            </div>
          </div>
          
          <div className="pdm-post-info-grid">
            {/* Common details */}
            <div className="pdm-post-detail">
              <span className="pdm-detail-icon"><FaCalendarAlt /></span>
              <div className="pdm-detail-content">
                <div className="pdm-detail-label">{postData.travel_type === 'recurring' ? t('recurring.schedule') : t('posts.date')}:</div>
                <div>{postData.travel_type === 'recurring' ? formatRecurrencePattern(postData) : formatDate(postData.travel_date)}</div>
              </div>
            </div>
            
            <div className="pdm-post-detail">
              <span className="pdm-detail-icon"><FaClock /></span>
              <div className="pdm-detail-content">
                <div className="pdm-detail-label">{t('posts.time')}:</div>
                <div>{postData.isRequest ? formatTime(new Date(postData.travel_date).toTimeString()) : formatTime(postData.travel_time)}</div>
              </div>
            </div>
            
            {postData.isRequest && postData.event && (
              <div className="pdm-post-detail">
                <span className="pdm-detail-icon"><FaInfoCircle /></span>
                <div className="pdm-detail-content">
                  <div className="pdm-detail-label">Event:</div>
                  <div>{postData.event.title}</div>
                </div>
              </div>
            )}
            
            {/* Post-specific details */}
            {!postData.isPackage && (
              <>
                {postData.car && (
                  <div className="pdm-post-detail">
                    <span className="pdm-detail-icon"><FaCar /></span>
                    <div className="pdm-detail-content">
                      <div className="pdm-detail-label">{t('posts.car')}:</div>
                      <div>{postData.car}</div>
                    </div>
                  </div>
                )}
                
                {postData.seats && (
                  <div className="pdm-post-detail">
                    <span className="pdm-detail-icon"><FaUser /></span>
                    <div className="pdm-detail-content">
                      <div className="pdm-detail-label">{t('posts.seats')}:</div>
                      <div>{postData.seats}</div>
                    </div>
                  </div>
                )}

                {/* Price Display - Shows Free badge or price */}
                {(postData.price || postData.is_free) && (
                  <div className="pdm-post-detail">
                    <span className="pdm-detail-icon"><FaEuroSign /></span>
                    <div className="pdm-detail-content">
                      <div className="pdm-detail-label">
                        {postData.role === 'Passenger' ? t('posts.price_offered') : t('posts.price_asked')}:
                      </div>
                      <div className="pdm-price-value">
                        {postData.is_free ? (
                          <span className="free-badge">{t('posts.free')}</span>
                        ) : (
                          `€${parseFloat(postData.price).toFixed(2)}`
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="pdm-post-detail">
                  <span className="pdm-detail-icon"><FaRoute /></span>
                  <div className="pdm-detail-content">
                    <div className="pdm-detail-label">{t('posts.drive_type')}:</div>
                    <div>
                      {postData.travel_type ?
                        t(`posts.${(postData.travel_type || '').toLowerCase().replace('-', '_')}`) :
                        t('posts.not_specified')}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Package-specific details */}
            {postData.isPackage && (
              <>
                <div className="pdm-post-detail">
                  <span className="pdm-detail-icon"><FaBox /></span>
                  <div className="pdm-detail-content">
                    <div className="pdm-detail-label">Size:</div>
                    <div>{postData.package_size}</div>
                  </div>
                </div>
                
                <div className="pdm-post-detail">
                  <span className="pdm-detail-icon"><FaWeightHanging /></span>
                  <div className="pdm-detail-content">
                    <div className="pdm-detail-label">Weight:</div>
                    <div>{postData.package_weight}</div>
                  </div>
                </div>
                
                <div className="pdm-post-detail">
                  <span className="pdm-detail-icon"><FaShippingFast /></span>
                  <div className="pdm-detail-content">
                    <div className="pdm-detail-label">Urgency:</div>
                    <div>{postData.delivery_urgency}</div>
                  </div>
                </div>

                {/* Delivery Cost Display */}
                {postData.delivery_cost && (
                  <div className="pdm-post-detail">
                    <span className="pdm-detail-icon"><FaEuroSign /></span>
                    <div className="pdm-detail-content">
                      <div className="pdm-detail-label">{t('posts.delivery_cost_offered')}:</div>
                      <div className="pdm-price-value">€{parseFloat(postData.delivery_cost).toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Description */}
            {postData.description && (
              <div className="pdm-post-detail pdm-full-width">
                <span className="pdm-detail-icon"><FaInfoCircle /></span>
                <div className="pdm-detail-content">
                  <div className="pdm-detail-label">Description:</div>
                  <div>{postData.description}</div>
                </div>
              </div>
            )}
            
            {/* Notes for ride requests */}
            {postData.isRequest && postData.notes && (
              <div className="pdm-post-detail pdm-full-width">
                <span className="pdm-detail-icon"><FaInfoCircle /></span>
                <div className="pdm-detail-content">
                  <div className="pdm-detail-label">Notes:</div>
                  <div>{postData.notes}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render the map section content
  const renderMapSection = () => {
    const isOwnPost = user?.id === postData?.user_id;
    
    if (isOwnPost && postData.route_polyline) {
      return (
        <div className="pdm-map-section">
          <PostRouteMapView
            post={postData}
            matches={matches}
            onViewMatch={handleMatchClick}
            onExpandChange={setIsMapExpanded}
            onMessageUser={(userId) => handleMessageClick({ user_id: userId })}
            onFilterChange={(filters) => {
              console.log('Filters changed:', filters);
              if (filters.searchRadius) {
                // Update the database and refresh matches with new radius
                const updateRadius = async () => {
                  try {
                    await supabase
                      .from(postData.isPackage ? 'packages' : 'posts')
                      .update({ search_radius_km: filters.searchRadius })
                      .eq('id', postData.id);

                    console.log('Radius updated in database to:', filters.searchRadius);

                    // Set new radius in state
                    setPostData(prev => ({
                      ...prev,
                      search_radius_km: filters.searchRadius
                    }));

                    // Fetch matches with the new radius immediately (pass as parameter)
                    fetchMatches(filters.searchRadius);
                  } catch (error) {
                    console.error('Error updating radius:', error);
                  }
                };

                updateRadius();
              }
            }}
            showRadiusSelector={true}
            initialRadius={postData.search_radius_km || 10}
          />
          
          {selectedMatch && (
            <div className="pdm-selected-match-container">
              <div
                className={`pdm-match-card ${selectedMatch.match_type}-card`}
              >
                {/* Close button to dismiss match card */}
                <CloseButton1
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMatch(null);
                  }}
                  className="pdm-match-close-button"
                  size="medium"
                />

                {/* Clickable area to open full details */}
                <div
                  className="pdm-match-clickable"
                  onClick={() => {
                    // Open a full details modal when clicking on the match card
                    if (selectedMatch && selectedMatch.match_id && selectedMatch.match_type) {
                      console.log('PostDetailsModal dispatching openPostDetailsModal event from MAP section:',
                        selectedMatch.match_id, selectedMatch.match_type);

                      // First close the current modal
                      onClose();

                      // Then dispatch event to open new modal after a short delay
                      setTimeout(() => {
                        const openModalEvent = new CustomEvent('openPostDetailsModal', {
                          detail: {
                            id: selectedMatch.match_id,
                            type: selectedMatch.match_type
                          }
                        });
                        window.dispatchEvent(openModalEvent);
                      }, 100);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                <div className="pdm-match-header">
                  <div className="pdm-match-user">
                    {selectedMatch.avatar_url ? (
                      <img src={selectedMatch.avatar_url} alt={selectedMatch.username} className="pdm-avatar" />
                    ) : (
                      <FaUser className="pdm-avatar-placeholder" />
                    )}
                    <span className="pdm-username">{selectedMatch.username}</span>
                  </div>
                  <div className="pdm-match-type">
                    {selectedMatch.match_role === 'Driver' && <FaCarSide className="pdm-role-icon pdm-driver" />}
                    {selectedMatch.match_role === 'Passenger' && <FaUser className="pdm-role-icon pdm-passenger" />}
                    {selectedMatch.match_role === 'Package' && <FaBox className="pdm-role-icon pdm-package" />}
                    <span>
                      {selectedMatch.match_role === 'Driver' && t('posts.driver')}
                      {selectedMatch.match_role === 'Passenger' && t('posts.passenger')}
                      {selectedMatch.match_role === 'Package' && t('posts.package')}
                    </span>
                  </div>
                </div>
                
                <div className="pdm-match-details">
                  <div className="pdm-match-detail">
                    <span>{t('posts.date')}: {new Date(selectedMatch.travel_date).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="pdm-match-detail">
                    <span>{t('posts.time')}: {selectedMatch.travel_time ? selectedMatch.travel_time.substring(0, 5) : ''}</span>
                  </div>
                  <div className="pdm-match-detail">
                    <span>{parseFloat(selectedMatch.distance_km || 0).toFixed(1)} km {t('posts.away')}</span>
                  </div>
                </div>
                </div> {/* Close pdm-match-clickable */}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="pdm-no-route-data-warning">
        <FaExclamationTriangle />
        <h3>{t('warnings.no_route_data_title') || 'Route Matching Not Available'}</h3>
        <p>
          {t('warnings.no_route_data_message') || 'This post does not have route data needed for matching. To enable matching, please recreate this post and make sure to calculate the route.'}
        </p>
      </div>
    );
  };
  
  // Render the posts section content (matches)
  const renderPostsSection = () => {
    const isOwnPost = user?.id === postData?.user_id;
    
    if (isOwnPost && postData.route_polyline) {
      return (
        <div className="pdm-posts-section">
          <RouteMatches
            routeId={postData.id}
            isPackage={postData.isPackage}
            role={null}
            radiusKm={postData.search_radius_km || 10}
            onSelectMatch={(match) => {
              // Instead of using handleMatchClick, dispatch the event directly
              if (match && match.match_id && match.match_type) {
                console.log('Dispatching openPostDetailsModal event from RouteMatches in PostDetailsModal:', 
                  match.match_id, match.match_type);
                  
                // Close current modal
                onClose();
                
                // Dispatch event to open new modal after a short delay
                setTimeout(() => {
                  const openModalEvent = new CustomEvent('openPostDetailsModal', {
                    detail: {
                      id: match.match_id,
                      type: match.match_type
                    }
                  });
                  window.dispatchEvent(openModalEvent);
                }, 100);
              }
            }}
            showAddressInsteadOfCoords={true}
            onRadiusChange={async (newRadius) => {
              // Update the database and refresh matches with new radius
              try {
                await supabase
                  .from(postData.isPackage ? 'packages' : 'posts')
                  .update({ search_radius_km: newRadius })
                  .eq('id', postData.id);

                console.log('Radius updated in database to:', newRadius);

                // Set new radius in state
                setPostData(prev => ({
                  ...prev,
                  search_radius_km: newRadius
                }));

                // Fetch matches with the new radius immediately (pass as parameter)
                fetchMatches(newRadius);

                addToast({
                  type: 'info',
                  title: t('matches.radius_updated') || 'Search Radius Updated',
                  description: t('matches.refreshing_matches') || 'Refreshing matches with new radius...'
                });
              } catch (error) {
                console.error('Error updating radius:', error);
                addToast({
                  type: 'error',
                  title: t('errors.update_failed') || 'Update Failed',
                  description: t('errors.radius_update_failed') || 'Failed to update search radius.'
                });
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="pdm-no-route-data-warning">
        <FaExclamationTriangle />
        <h3>{t('warnings.no_route_data_title') || 'Route Matching Not Available'}</h3>
        <p>
          {t('warnings.no_route_data_message') || 'This post does not have route data needed for matching. To enable matching, please recreate this post and make sure to calculate the route.'}
        </p>
      </div>
    );
  };
  
  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t('app.loading')}>
        <div className="pdm-post-details-loading">
          <Spinner size="medium" customText={t('app.loading')} />
        </div>
      </Modal>
    );
  }
  
  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t('errors.error')}>
        <div className="pdm-post-details-error">
          <div className="pdm-error-message">
            <FaExclamationTriangle />
            <p>{error}</p>
          </div>
        </div>
      </Modal>
    );
  }
  
  if (!postData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={t('errors.not_found')}>
        <div className="pdm-post-details-error">
          <div className="pdm-error-message">
            <FaExclamationTriangle />
            <p>{t('posts.not_found')}</p>
          </div>
        </div>
      </Modal>
    );
  }
  
  const isOwnPost = user?.id === postData.user_id;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={getModalTitle()}
        modalSize={isMapExpanded ? 'fullscreen' : 'large'}
      >
          <div className={`pdm-post-card ${postData.isPackage ? 'pdm-package-card' : 'pdm-regular-card'} ${isMapExpanded ? 'pdm-map-expanded' : ''}`}>

          {/* Check if we should show additional views (map/matches) for own posts */}
          {showMapView ? (
            <div className="pdm-additional-view">
              <button
                className="pdm-back-to-details"
                onClick={() => {
                  setShowMapView(false);
                  setSelectedMatch(null);
                  setIsMapExpanded(false);
                }}
              >
                <FaInfoCircle className="pdm-nav-icon" />
                {t('posts.back_to_details') || 'Back to Details'}
              </button>
              {renderMapSection()}
            </div>
          ) : showMatchesView ? (
            <div className="pdm-additional-view">
              <button
                className="pdm-back-to-details"
                onClick={() => {
                  setShowMatchesView(false);
                  setSelectedMatch(null);
                }}
              >
                <FaInfoCircle className="pdm-nav-icon" />
                {t('posts.back_to_details') || 'Back to Details'}
              </button>
              {renderPostsSection()}
            </div>
          ) : (
            /* Main content with pagination */
            <PostCardPagination
              userId={postData.user_id}
              username={postData.profiles?.username || t('chat.unknown_user')}
              post={postData}
              headerContent={
                <div className="pagination-header-content">
                  <div className="pdm-p-user-name">
                    <img
                      src={postData.profiles?.avatar_url || '/default-avatar.png'}
                      alt={postData.profiles?.username || t('chat.unknown_user')}
                      className="pdm-p-avatar"
                    />
                    <h4>{postData.profiles?.username || t('chat.unknown_user')}</h4>
                    {/* Role/type badge — keeps the driver/passenger/package identity
                        visible after the card's badge is replaced by the modal. */}
                    <span
                      className={`pdm-role-badge pdm-role-badge--${
                        postData.isPackage ? 'package' : (postData.role || 'driver').toLowerCase()
                      }`}
                    >
                      {postData.isPackage ? (
                        <FaBox />
                      ) : postData.role === 'Passenger' ? (
                        <FaUser />
                      ) : (
                        <FaCarSide />
                      )}
                      <span>
                        {postData.isPackage
                          ? t('posts.package')
                          : postData.role === 'Passenger'
                            ? t('posts.passenger')
                            : t('posts.driver')}
                      </span>
                    </span>
                  </div>

                  {/* Action buttons moved to header - for better mobile UX */}
                  <div className="pdm-header-actions">
                    {/* Message button - only show for other users' posts */}
                    {user && user.id !== postData.user_id ? (
                      <div className="post-actions">
                        <CustomButton7
                          label={t('chat.send_message')}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMessageClick(postData);
                          }}
                          disabled={messageConfirmModal.isLoading}
                        >
                          {messageConfirmModal.isLoading ? <Spinner size="small" showText={false} /> : <FaRegPaperPlane size={18} />}
                        </CustomButton7>
                      </div>
                    ) : isOwnPost && postData.route_polyline ? (
                      /* Additional navigation for own posts with route data */
                      <div className="pdm-own-post-actions">
                        <div className="pdm-own-post-actions-fr">
                          <button
                            className="pdm-secondary-nav-button"
                            onClick={() => setShowMapView(true)}
                            title={t('posts.view_on_map') || 'View on Map'}
                          >
                            <FaMapMarkedAlt className="pdm-nav-icon" />
                            {t('posts.map') || 'Map'}
                          </button>
                        </div>

                        <div className="pdm-own-post-actions-fr">
                          <button
                            className="pdm-secondary-nav-button"
                            onClick={() => setShowMatchesView(true)}
                            title={t('posts.view_matches') || 'View Matches'}
                          >
                            <FaListAlt className="pdm-nav-icon" />
                            {t('posts.matches') || 'Matches'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="pdm-own-post-badge">{t('posts.your_post')}</span>
                    )}
                  </div>
                </div>
              }
              footerContent={null}
            >
              {/* Page 0 content: Post details */}
              {renderPostContent()}
            </PostCardPagination>
          )}
        </div>
      </Modal>

      {/* Date Selection Modal for recurring posts */}
      <DateSelectionModal
        isOpen={dateSelectionModal.isOpen}
        onClose={() => setDateSelectionModal({ isOpen: false, post: null })}
        onSelectDate={handleDateSelected}
        post={dateSelectionModal.post}
      />

      {/* Message Confirmation Modal */}
      <MessageConfirmationModal
        isOpen={messageConfirmModal.isOpen}
        onClose={() => setMessageConfirmModal({ isOpen: false, post: null, isLoading: false })}
        onConfirm={handleConfirmMessage}
        post={messageConfirmModal.post}
        isLoading={messageConfirmModal.isLoading}
      />
    </>
  );
}